/**
 * Card Configuration
 * Centralized configuration for all card types in the application
 */

export interface CardTypeConfig {
    type: string;
    title: string;
    content: string;
    icon: string;
    description?: string;
}

/**
 * Available card types with their configurations
 */
export const CARD_TYPE_CONFIGS: CardTypeConfig[] = [
    {
        type: 'portfolio',
        title: 'Portfolio Overview',
        content: 'portfolio',
        icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
        description: 'View your portfolio metrics and performance'
    },
    {
        type: 'market',
        title: 'Market Watch',
        content: 'market',
        icon: 'M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z',
        description: 'Monitor real-time market data and watch lists'
    },
    {
        type: 'trades',
        title: 'Recent Trades',
        content: 'trades',
        icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2',
        description: 'View your recent trading activity'
    },
    {
        type: 'funds',
        title: 'Funds',
        content: 'funds',
        icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
        description: 'View available funds and margin from connected providers'
    },
    {
        type: 'analytics',
        title: 'Analytics',
        content: 'analytics',
        icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
        description: 'Trading analytics and insights'
    },
    {
        type: 'news',
        title: 'Market News',
        content: 'news',
        icon: 'M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z',
        description: 'Latest market news and updates'
    },
    {
        type: 'charts',
        title: 'Stock Charts',
        content: 'charts',
        icon: 'M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z',
        description: 'Advanced charting and technical analysis'
    },
    {
        type: 'chat',
        title: 'AI Assistant',
        content: 'chat',
        icon: 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z',
        description: 'Chat with AI assistant and send voice messages'
    },
    {
        type: 'connection-warning',
        title: 'Connection Required',
        content: 'connection-warning',
        icon: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z',
        description: 'Connect to a data provider to access features'
    }
];

/**
 * Get card type configuration by type
 */
export const getCardTypeConfig = (type: string): CardTypeConfig | undefined => {
    return CARD_TYPE_CONFIGS.find(config => config.type === type);
};

/**
 * Get all available card types as a map for easy lookup
 */
export const CARD_TYPES_MAP = CARD_TYPE_CONFIGS.reduce((acc, config) => {
    acc[config.type] = config;
    return acc;
}, {} as { [key: string]: CardTypeConfig });
